import React from 'react'
import PropTypes from 'prop-types'
import TrackingPixel from '../../components/TrackingPixel'
import * as Trackers from '../../components/Trackers'
import ScriptLoader from '../../components/ScriptLoader'
import PreviewBannerContainer from '../../containers/PreviewBanner'
import merge from 'deepmerge'
import { ThemeProvider } from 'pcln-design-system'
import isMobileDevice from '../../utils/isMobileDevice'
import { applicationStateShape, partnerShape } from '../../utils/shapes'
// eslint-disable-next-line import/no-named-as-default
import Helmet from 'react-helmet'
import replaceApplicationStatePlaceholders from '../../utils/replaceApplicationStatePlaceholders'
import isLocalStorageEnabled from '../../utils/isLocalStorageEnabled'
import { getPageType, getType } from '../../utils/getPageType'
import SSOProvider from '../SSOProvider'
import CUGProvider from '../CUGProvider'

export const PartnerContext = React.createContext()
export const PartnerConsumer = PartnerContext.Consumer

const UPDATE_PARTNER_TRACKING_PIXEL = 'UPDATE_PARTNER_TRACKING_PIXEL'
const UPDATE_PARTNER_APPLICATION_STATE = 'UPDATE_PARTNER_APPLICATION_STATE'
const UPDATE_PARTNER_OMITTED = 'UPDATE_PARTNER_OMITTED'

const checkOmit = (omit, component) => (Array.isArray(omit) && omit.includes(component)) || omit === true

const renderEveryMundo = (config, applicationState, appName) =>
  (config.state
    ? Object.keys(config.state).every((key) => applicationState[key] === config.state[key])
    : true) && config.applications.includes(appName)

const pixelTypes = {
  ...Trackers,
  image: TrackingPixel,
  iframe: TrackingPixel,
  script: TrackingPixel,
}

/**
 * A provider that can be used to wrap your application and set a partner
 *
 * @param {Object} partner
 * @param {*}      children
 *
 * @returns {null|React.Component}
 */
class PartnerProvider extends React.Component {
  constructor(props) {
    super(props)
    const trackingPixelParameters = props?.trackingPixelParameters ?? {}
    const applicationState = props?.applicationState ?? {}
    this.state = {
      omit: {
        trackingPixels: checkOmit(props.omit, 'trackingPixels') || !(props?.renderTrackingPixel ?? true),
        headScripts: checkOmit(props.omit, 'headScripts'),
      },
      applicationState: merge.all([
        PartnerProvider.getApplicationState(),
        trackingPixelParameters,
        applicationState,
      ]),
      prevApplicationProps: merge(trackingPixelParameters, applicationState),
    }
    this.shouldOmit = this.shouldOmit.bind(this)
    this.updateOmit = this.updateOmit.bind(this)
    this.updateTrackingPixel = this.updateTrackingPixel.bind(this)
    this.updateWlTracking = this.updateWlTracking.bind(this)
    this.updateApplicationState = this.updateApplicationState.bind(this)
    this.callTrackingEvent = this.callTrackingEvent.bind(this)
    this.clearTrackingEvent = this.clearTrackingEvent.bind(this)
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const nextState = {
      applicationState: {
        ...prevState.applicationState,
        random: PartnerProvider.getApplicationState().random,
        pageType: PartnerProvider.getApplicationState().pageType,
        type: PartnerProvider.getApplicationState().type,
      },
      prevApplicationProps: {
        ...prevState.prevApplicationProps,
      },
    }

    if (typeof nextProps.trackingPixelParameters === 'object') {
      Object.keys(nextProps.trackingPixelParameters).forEach((key) => {
        if (nextProps.trackingPixelParameters[key] !== prevState.prevApplicationProps[key]) {
          nextState.applicationState[key] = nextProps.trackingPixelParameters[key]
          nextState.prevApplicationProps[key] = nextProps.trackingPixelParameters[key]
        }
      })
    }

    if (typeof nextProps.applicationState === 'object') {
      Object.keys(nextProps.applicationState).forEach((key) => {
        if (
          !['random', 'pageType', 'type'].includes(key) &&
          nextProps.applicationState[key] !== prevState.prevApplicationProps[key]
        ) {
          nextState.applicationState[key] = nextProps.applicationState[key]
          nextState.prevApplicationProps[key] = nextProps.applicationState[key]
        }
      })
    }

    return nextState
  }

  shouldOmit(component) {
    return this.state.omit[component]
  }

  clearTrackingEvent() {
    this.updateApplicationState({
      ...this.state.applicationState,
      renderEvent: undefined,
    })
  }

  callTrackingEvent(event, trackingPixelParameters = this.state.applicationState) {
    this.updateApplicationState({
      ...trackingPixelParameters,
      renderEvent: event,
      clearRenderEvent: this.clearTrackingEvent,
    })
  }

  updateTrackingPixel({
    renderTrackingPixel = this.shouldOmit('trackingPixels') === false,
    trackingPixelParameters = this.state.applicationState,
  }) {
    this.updateApplicationState(trackingPixelParameters)
    this.updateOmit({ trackingPixels: renderTrackingPixel === false })
  }

  static getApplicationState() {
    const navigator = global?.window?.navigator ?? {}
    const deviceType = isMobileDevice(navigator) ? 'mobile' : 'desktop'

    const language = (
      (isLocalStorageEnabled() && window.localStorage.getItem('i18nextLng')) ||
      'en-us'
    ).toLowerCase()
    /* istanbul ignore next */
    const currentURL = global?.window?.location?.href ?? ''
    const pageType = getPageType(currentURL)

    return {
      advancedPurchase: null,
      amount: null,
      amountInt: null,
      checkInDate: null,
      checkOutDate: null,
      components: null,
      currency: null,
      currentURL,
      date: new Date(),
      departingDate: null,
      destination: null,
      destinationAirportCode: null,
      device: deviceType,
      email: null,
      endDate: null,
      firstName: null,
      flyConfirmationNumber: null,
      flyEndDate: null,
      flyStartDate: null,
      hotelName: null,
      lang: language,
      numAdults: null,
      numPassengers: null,
      orderId: null,
      origin: null,
      originAirportCode: null,
      pageType,
      payNowAmount: null,
      payNowValue: null,
      productType: null,
      random: Math.floor(Math.random() * Date.now()) + Date.now(),
      type: getType(pageType),
      value: null,
    }
  }

  updateApplicationState(applicationState) {
    this.setState((prevState) => ({
      applicationState: {
        ...prevState.applicationState,
        ...applicationState,
      },
    }))
  }

  updateOmit(omitted) {
    this.setState((prevState) => ({
      omit: {
        ...prevState.omit,
        ...omitted,
      },
    }))
  }

  updateWlTracking() {
    const { wlTracking: payload } = this.props.partner || {}
    const { wlTracking: wlTrackingState = '' } = this.state

    if (typeof window === 'object' && payload && Object.keys(payload).length !== 0) {
      const wlSource = payload.wl_source || ''
      const wlTrackingData = JSON.stringify(payload).replace(/"/g, "'")

      if (wlTrackingData !== wlTrackingState) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'wl_tracking',
          wl_source: wlSource,
          wl_tracking_params: wlTrackingData,
        })

        this.setState({ wlTracking: wlTrackingData })
      }
    }
  }

  componentDidMount() {
    // Listen for events
    this.updateWlTracking()

    window.addEventListener(UPDATE_PARTNER_TRACKING_PIXEL, this.updateTrackingPixel)
    window.addEventListener(UPDATE_PARTNER_APPLICATION_STATE, this.updateApplicationState)
    window.addEventListener(UPDATE_PARTNER_OMITTED, this.updateOmit)
  }

  componentDidUpdate() {
    this.updateWlTracking()
  }

  componentWillUnmount() {
    // Remove listeners
    window.removeEventListener(UPDATE_PARTNER_TRACKING_PIXEL, this.updateTrackingPixel)
    window.removeEventListener(UPDATE_PARTNER_APPLICATION_STATE, this.updateApplicationState)
    window.removeEventListener(UPDATE_PARTNER_OMITTED, this.updateOmit)
  }

  render() {
    const { appName, isCugUnlocked, partner, children, env } = this.props
    const { applicationState } = this.state

    if (!children) {
      return null
    }

    if (!partner || !partner.config) {
      return children
    }

    const theme = merge(partner?.config?.theme ?? {}, {
      flyQueries: {
        mobile: '@media screen and (max-width: 413px)',
        mobilelg: '@media screen and (min-width: 414px)',
        tablet: '@media screen and (min-width: 600px)',
        tabletlg: '@media screen and (min-width: 768px)',
        tablethd: '@media screen and (min-width: 840px)',
        desktop: '@media screen and (min-width: 1024px)',
        desktoplg: '@media screen and (min-width: 1280px)',
        desktophd: '@media screen and (min-width: 1440px)',
      },
    })

    const everyMundo = partner?.config?.everyMundo
    const isEnvValid = env && (env === 'qa' || env === 'prod' || env === 'preprod')
    const trackingPixels = partner?.config?.trackingPixels ?? []

    return (
      <PartnerContext.Provider
        value={{
          appName,
          ...partner,
          env,
          updateTrackingPixel: this.updateTrackingPixel,
          updateApplicationState: this.updateApplicationState,
          updateOmit: this.updateOmit,
          callTrackingEvent: this.callTrackingEvent,
        }}
      >
        {!this.shouldOmit('trackingPixels') &&
          isEnvValid &&
          trackingPixels.map((pixel, idx) => {
            const type = pixel.type
            if (pixelTypes[type]) {
              const Pixel = pixelTypes[type]
              return (
                <Pixel
                  key={type + idx}
                  env={env}
                  {...pixel.props}
                  type={type}
                  applicationState={applicationState}
                />
              )
            }
          })}
        {!this.shouldOmit('headScripts') &&
          (partner?.config?.head?.scripts ?? []).map((script, idx) =>
            typeof script === 'string' ? (
              <ScriptLoader
                data-testid={'head-script-' + idx}
                key={script}
                url={script}
                applicationState={applicationState}
              />
            ) : (
              script.applications?.includes(appName) && (
                <ScriptLoader
                  data-testid={'head-script-' + idx}
                  key={script.url}
                  url={script.url}
                  applicationState={applicationState}
                />
              )
            )
          )}
        {env === 'prod' &&
          !this.shouldOmit('headScripts') &&
          everyMundo &&
          (Array.isArray(everyMundo)
            ? everyMundo.map(
                (emConfig, idx) =>
                  renderEveryMundo(emConfig, applicationState, appName) && (
                    <ScriptLoader
                      key={`everyMundo-${idx}`}
                      data-testid={`everymundo-${idx}`}
                      url={emConfig.script}
                    />
                  )
              )
            : renderEveryMundo(everyMundo, applicationState, appName) && (
                <ScriptLoader key='everyMundo' data-testid='everymundo' url={everyMundo.script} />
              ))}
        {!this.shouldOmit('headScripts') && partner?.config?.head?.links && (
          <Helmet>
            {partner.config.head.links.map(({ href, ...props }, i) => (
              <link
                key={i}
                {...props}
                href={href && replaceApplicationStatePlaceholders(href, applicationState)}
              />
            ))}
          </Helmet>
        )}
        <ThemeProvider theme={theme}>
          <SSOProvider env={env}>
            <CUGProvider isCugUnlocked={isCugUnlocked} env={env}>
              {children}
              <PreviewBannerContainer url={applicationState?.currentURL} />
            </CUGProvider>
          </SSOProvider>
        </ThemeProvider>
      </PartnerContext.Provider>
    )
  }
}

PartnerProvider.defaultProps = {
  isCugUnlocked: false,
  omit: false,
}

PartnerProvider.propTypes = {
  applicationState: applicationStateShape,
  appName: PropTypes.string,
  children: PropTypes.node,
  isCugUnlocked: PropTypes.bool,
  omit: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOf(['trackingPixels', 'headScripts'])),
  ]),
  partner: partnerShape,
  renderTrackingPixel: PropTypes.bool,
  trackingPixelParameters: PropTypes.object,
  env: (props, propName, componentName) => {
    if (props['partner'] !== undefined && props[propName] === undefined) {
      return new Error(
        // prettier-ignore
        `The prop \`${propName}\` is marked as required in \`${componentName}\` when a partner exists, but its value is \`undefined\`.`
      )
    }
    if (typeof props[propName] !== 'string') {
      return new Error(
        // prettier-ignore
        `Invalid prop \`${propName}\` of type \`${typeof props[propName]}\` supplied to \`${componentName}\`, expected \`string\`.`
      )
    }
  },
}

PartnerProvider.displayName = 'PartnerProvider'
PartnerContext.displayName = 'PartnerContext'

export default PartnerProvider
